// src/pages/logistica/CostosPT.jsx
// Costo por pieza de producto terminado (Excel mensual) para el KPI de
// Caducidades, y rangos del semáforo.
// Pestañas: Cargar archivo / Historial / Mapeo de productos / Semáforo / Bitácora.
import { useEffect, useMemo, useRef, useState } from 'react';
import { Chart, registerables } from 'chart.js';
import * as XLSX from 'xlsx';
import { FiUpload, FiDownload, FiInfo, FiX, FiAlertTriangle } from 'react-icons/fi';
import api from '../../services/api';
import s from '../mantenimiento/estilos';

Chart.register(...registerables);

const BASE = '/api/logistica/costos-pt';
const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
const PALETA = ['#4A1E5C', '#B8862E', '#3E7C5A', '#4F7CA8', '#B23A48', '#A98BB8', '#6E4F80', '#D6A644'];
const pesos = (v, dec = 2) => (v === null || v === undefined ? '—' : `$${Number(v).toLocaleString('es-MX', { minimumFractionDigits: dec, maximumFractionDigits: dec })}`);
const nombreMes = (d) => new Date(d).toLocaleDateString('es-MX', { month: 'long', year: 'numeric', timeZone: 'UTC' });
const fmtFechaHora = (d) => (d ? new Date(d).toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' }) : '—');

/** Lee el Excel de costos: detecta el mes ("Agosto 2026") y las filas nombre + costo (+ canal de la primera columna) */
function leerExcel(libro) {
  const hoja = libro.Sheets[libro.SheetNames[0]];
  const filas = XLSX.utils.sheet_to_json(hoja, { header: 1, raw: true, defval: null });
  let mes = null;
  const productos = [];
  let canal = null;
  for (const fila of filas) {
    for (const celda of fila) {
      const m = typeof celda === 'string' && celda.toLowerCase().match(/(enero|febrero|marzo|abril|mayo|junio|julio|agosto|septiembre|setiembre|octubre|noviembre|diciembre)\s+(?:de\s+)?(\d{4})/);
      if (m && !mes) {
        const n = m[1] === 'setiembre' ? 9 : MESES.indexOf(m[1]) + 1;
        mes = `${m[2]}-${String(n).padStart(2, '0')}-01`;
      }
    }
    const iNum = fila.map((c, i) => (typeof c === 'number' ? i : -1)).filter((i) => i >= 0).pop();
    if (iNum === undefined) {
      if (typeof fila[0] === 'string' && fila[0].trim() && fila.filter((c) => c !== null).length === 1) canal = fila[0].trim();
      continue;
    }
    const textos = fila.slice(0, iNum).map((c, i) => ({ c, i })).filter((x) => typeof x.c === 'string' && x.c.trim());
    if (!textos.length) continue;
    const nombre = textos[textos.length - 1].c.trim();
    if (textos.length > 1 || (fila[0] && textos[0].i === 0 && textos.length > 1)) canal = textos[0].c.trim();
    else if (typeof fila[0] === 'string' && fila[0].trim() && textos[0].i !== 0) canal = fila[0].trim();
    productos.push({ nombre, costo: fila[iNum], canal });
  }
  return { mes, productos };
}

function Grafica({ config, alto = 280 }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current) return undefined;
    const chart = new Chart(ref.current, config);
    return () => chart.destroy();
  }, [config]);
  return <div style={{ position: 'relative', height: alto }}><canvas ref={ref} /></div>;
}

export default function CostosPT() {
  const [tab, setTab] = useState('cargar');
  const [mensaje, setMensaje] = useState(null);
  const [mapeo, setMapeo] = useState({ data: [], nombres_excel: [] });
  const [historial, setHistorial] = useState({ meses: [], data: [] });
  const [semaforo, setSemaforo] = useState({ data: null, historial: [] });
  const [cargas, setCargas] = useState([]);

  async function cargarTodo() {
    const [m, h, sm, c] = await Promise.all([
      api.get(`${BASE}/mapeo`), api.get(`${BASE}/historial`), api.get(`${BASE}/semaforo`), api.get(`${BASE}/cargas`),
    ]);
    setMapeo(m.data);
    setHistorial(h.data);
    setSemaforo(sm.data);
    setCargas(c.data.data);
  }

  useEffect(() => {
    cargarTodo().catch((err) => {
      console.error(err);
      setMensaje({ tipo: 'error', texto: 'No se pudo cargar la información. Revisa que la ruta /api/logistica/costos-pt esté montada.' });
    });
  }, []);

  const errorTexto = (err, def) => (err.response?.status === 403 ? 'No tienes permiso de captura en Logística.' : err.response?.data?.error || def);

  return (
    <div style={s.paginaContenedor} className="contenedor-captura">
      <div style={{ marginBottom: 14 }}>
        <h1 style={{ ...s.tituloPagina, marginBottom: 2 }}>Costos de producto terminado</h1>
        <div style={{ fontSize: 12, color: '#8A7690' }}>Costo por pieza para calcular el valor en riesgo de Caducidades · se carga cada mes desde Excel</div>
      </div>

      {mensaje && (
        <div style={{ ...s.mensaje, color: mensaje.tipo === 'ok' ? '#3E7C5A' : '#B23A48', display: 'flex', justifyContent: 'space-between' }}>
          {mensaje.texto}
          <button style={s.linkChico} onClick={() => setMensaje(null)}>cerrar</button>
        </div>
      )}

      <div style={s.tabBar}>
        {[['cargar', 'Cargar archivo'], ['historial', 'Historial de costos'], ['mapeo', 'Mapeo de productos'], ['semaforo', 'Semáforo'], ['bitacora', 'Bitácora']].map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} style={{ ...s.tabBtn, ...(tab === k ? s.tabBtnActivo : {}) }}>{label}</button>
        ))}
      </div>

      {tab === 'cargar' && <TabCargar mapeo={mapeo} onListo={async (texto) => { setMensaje({ tipo: 'ok', texto }); await cargarTodo(); }} onError={(err, def) => setMensaje({ tipo: 'error', texto: errorTexto(err, def) })} />}
      {tab === 'historial' && <TabHistorial historial={historial} />}
      {tab === 'mapeo' && <TabMapeo mapeo={mapeo} onCambio={cargarTodo} onMensaje={setMensaje} errorTexto={errorTexto} />}
      {tab === 'semaforo' && <TabSemaforo semaforo={semaforo} onCambio={cargarTodo} onMensaje={setMensaje} errorTexto={errorTexto} />}
      {tab === 'bitacora' && <TabBitacora cargas={cargas} />}
    </div>
  );
}

// ═══════════ CARGAR ARCHIVO ═══════════
function TabCargar({ mapeo, onListo, onError }) {
  const [archivo, setArchivo] = useState(null);
  const [leido, setLeido] = useState(null);
  const [mes, setMes] = useState('');
  const [previa, setPrevia] = useState(null);
  const [asignaciones, setAsignaciones] = useState({}); // nombre del Excel → [artículo]
  const [revisando, setRevisando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [aviso, setAviso] = useState(null);
  const inputRef = useRef(null);

  const hoy = new Date();
  const opcionesMes = Array.from({ length: 18 }, (_, i) => {
    const d = new Date(hoy.getFullYear(), hoy.getMonth() - i + 1, 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
  });

  // Artículos del inventario que todavía no tienen nombre del Excel (primero) y luego el resto
  const articulos = useMemo(() => [...mapeo.data].filter((a) => a.en_inventario)
    .sort((a, b) => (a.nombre_excel ? 1 : 0) - (b.nombre_excel ? 1 : 0) || String(a.articulo).localeCompare(String(b.articulo), 'es')), [mapeo]);

  async function revisar(m = mes, l = leido) {
    if (!m || !l) return;
    try {
      setRevisando(true);
      const res = await api.post(`${BASE}/previsualizar`, { mes: m, filas: l.productos });
      setPrevia(res.data.data);
    } catch (err) {
      console.error(err);
      onError(err, 'No se pudo revisar el archivo.');
    } finally {
      setRevisando(false);
    }
  }

  async function alElegir(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    setAviso(null);
    setPrevia(null);
    setAsignaciones({});
    try {
      const libro = XLSX.read(await f.arrayBuffer(), { type: 'array' });
      const l = leerExcel(libro);
      if (!l.productos.length) {
        setAviso('No encontré productos con costo en el archivo. Debe tener el nombre del producto y su costo por pieza en la misma fila.');
        return;
      }
      setArchivo(f.name);
      setLeido(l);
      setMes(l.mes || '');
      if (l.mes) revisar(l.mes, l);
      else setAviso('No encontré el mes en el archivo (por ejemplo "Agosto 2026"). Elígelo abajo.');
    } catch (err) {
      console.error(err);
      setAviso('No se pudo leer el archivo. ¿Es un Excel (.xlsx)?');
    } finally {
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  function asignar(nombre, articulo) {
    if (!articulo) return;
    setAsignaciones((prev) => ({ ...prev, [nombre]: [...new Set([...(prev[nombre] || []), articulo])] }));
  }
  function quitar(nombre, articulo) {
    setAsignaciones((prev) => ({ ...prev, [nombre]: (prev[nombre] || []).filter((u) => u !== articulo) }));
  }

  const sinMapear = previa ? previa.filas.filter((f) => !f.mapeado && !(asignaciones[f.nombre] || []).length) : [];
  const usadosEnAsignacion = new Set(Object.values(asignaciones).flat());

  async function guardar() {
    if (sinMapear.length && !window.confirm(`${sinMapear.length} producto(s) del archivo no están ligados a ningún artículo. Su costo se guarda, pero no se aplicará a Caducidades hasta mapearlos. ¿Continuar?`)) return;
    if (previa.ya_existia && !window.confirm(`Ya hay costos cargados de ${nombreMes(mes)}. Se reemplazarán por los de este archivo. ¿Continuar?`)) return;
    try {
      setGuardando(true);
      const mapeos = Object.entries(asignaciones).flatMap(([nombre_excel, arts]) => arts.map((articulo) => ({ articulo, nombre_excel })));
      const res = await api.post(`${BASE}/cargar`, { mes, archivo, filas: leido.productos, mapeos });
      const d = res.data.data;
      await onListo(`✓ Costos de ${nombreMes(d.mes)} cargados: ${d.productos} productos${d.reemplazo ? ' (reemplazó la carga anterior de ese mes)' : ''}.`
        + (d.inventario_sin_costo ? ` Quedan ${d.inventario_sin_costo} artículo(s) del inventario sin costo en este archivo.` : ''));
      setLeido(null);
      setPrevia(null);
      setArchivo(null);
      setAsignaciones({});
    } catch (err) {
      console.error(err);
      onError(err, 'No se pudo guardar el archivo.');
    } finally {
      setGuardando(false);
    }
  }

  return (
    <>
      <div style={s.tarjeta}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <input ref={inputRef} type="file" accept=".xlsx,.xls" onChange={alElegir} style={{ display: 'none' }} />
          <button onClick={() => inputRef.current?.click()} style={{ ...s.botonGuardarChico, display: 'flex', alignItems: 'center', gap: 6 }}>
            <FiUpload /> Elegir Excel de costos
          </button>
          {archivo && <span style={{ fontSize: 13, color: '#2A1830' }}>{archivo} · {leido.productos.length} productos</span>}
          {leido && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#6E4F80', fontWeight: 600 }}>
              Mes del costo
              <select value={mes} onChange={(e) => { setMes(e.target.value); revisar(e.target.value); }} style={s.filtroInput}>
                <option value="">— Elige —</option>
                {opcionesMes.map((m) => <option key={m} value={m}>{nombreMes(m)}</option>)}
              </select>
            </label>
          )}
        </div>
        {aviso && <div style={{ ...s.avisoAdvertencia, marginBottom: 0 }}>{aviso}</div>}
        {!leido && (
          <div style={{ fontSize: 12, color: '#8A7690', marginTop: 10 }}>
            Sube el archivo tal como lo envía Finanzas: una fila por producto con su costo por pieza y el mes en el encabezado (por ejemplo "Agosto 2026").
            Cada mes se guarda aparte, así que el historial no se pierde.
          </div>
        )}
      </div>

      {revisando && <div style={s.subtitulo}>Revisando el archivo...</div>}

      {previa && !revisando && (
        <>
          {previa.ya_existia && (
            <div style={{ ...s.avisoAdvertencia, marginTop: 0, marginBottom: 12 }}>
              <FiAlertTriangle /> Ya hay costos cargados de {nombreMes(mes)}. Si guardas, se reemplazan por los de este archivo.
            </div>
          )}

          <div style={s.tablaScroll}>
            <table style={s.tabla}>
              <thead>
                <tr>
                  <th style={s.th}>Producto (Excel)</th>
                  <th style={s.th}>Canal</th>
                  <th style={{ ...s.th, textAlign: 'right' }}>Costo / pieza</th>
                  <th style={{ ...s.th, textAlign: 'right' }}>Mes anterior</th>
                  <th style={{ ...s.th, textAlign: 'right' }}>Variación</th>
                  <th style={s.th}>Artículo(s) de NetSuite</th>
                </tr>
              </thead>
              <tbody>
                {previa.filas.map((f) => {
                  const nuevos = asignaciones[f.nombre] || [];
                  return (
                    <tr key={f.nombre} style={!f.mapeado && !nuevos.length ? { background: '#FFF8EC' } : undefined}>
                      <td style={{ ...s.td, fontWeight: 600, whiteSpace: 'normal' }}>{f.nombre}</td>
                      <td style={s.td}>{f.canal || '—'}</td>
                      <td style={{ ...s.td, textAlign: 'right', fontWeight: 700 }}>{pesos(f.costo)}</td>
                      <td style={{ ...s.td, textAlign: 'right' }}>{f.costo_anterior === null ? '—' : pesos(f.costo_anterior)}</td>
                      <td style={{ ...s.td, textAlign: 'right', fontWeight: f.revisar ? 700 : 400, color: f.revisar ? '#B23A48' : f.variacion_pct > 0 ? '#B8862E' : f.variacion_pct < 0 ? '#3E7C5A' : '#8A7690' }}>
                        {f.variacion_pct === null ? 'nuevo' : `${f.variacion_pct > 0 ? '+' : ''}${f.variacion_pct}%`}
                        {f.revisar && <div style={{ fontSize: 10, fontWeight: 600 }}>revisar</div>}
                      </td>
                      <td style={{ ...s.td, whiteSpace: 'normal', minWidth: 260 }}>
                        {f.articulos.map((u) => (
                          <div key={u.articulo} style={{ fontSize: 12 }}>{u.articulo}{!u.en_inventario && <span style={x.sub}> · sin inventario hoy</span>}</div>
                        ))}
                        {nuevos.map((art) => {
                          const a = articulos.find((x2) => x2.articulo === art);
                          return (
                            <div key={art} style={x.chip}>
                              {art} <span style={x.sub}>· {a?.codigo_upc}</span>
                              <button style={x.chipX} onClick={() => quitar(f.nombre, art)} aria-label="Quitar"><FiX size={11} /></button>
                            </div>
                          );
                        })}
                        {!f.mapeado && (
                          <select value="" onChange={(e) => asignar(f.nombre, e.target.value)} style={{ ...s.inputChico, marginTop: 4, maxWidth: 320 }}>
                            <option value="">{nuevos.length ? '+ Ligar otro artículo…' : '— Ligar a un artículo —'}</option>
                            {articulos.filter((a) => !usadosEnAsignacion.has(a.articulo)).map((a) => (
                              <option key={a.articulo} value={a.articulo}>
                                {a.nombre_excel ? '' : '● '}{a.articulo} ({a.codigo_upc}){a.nombre_excel ? ` — hoy: ${a.nombre_excel}` : ''}
                              </option>
                            ))}
                          </select>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div style={{ fontSize: 11, color: '#8A7690', marginTop: 6 }}>● = artículo del inventario sin costo todavía. Los productos ya ligados se reconocen solos cada mes.</div>

          {previa.inventario_sin_costo_en_archivo.length > 0 && (
            <div style={{ ...s.avisoAdvertencia, marginTop: 12 }}>
              <strong>{previa.inventario_sin_costo_en_archivo.length} artículo(s) del inventario no tienen costo en este archivo</strong> (o no están ligados):{' '}
              {previa.inventario_sin_costo_en_archivo.slice(0, 8).map((a) => a.articulo).join(', ')}
              {previa.inventario_sin_costo_en_archivo.length > 8 ? '…' : ''}. Conservarán el costo de su último mes cargado, si tienen.
            </div>
          )}

          <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 14 }}>
            <button onClick={guardar} disabled={guardando || !mes} style={s.botonGuardarChico}>
              {guardando ? 'Guardando...' : `Guardar costos de ${mes ? nombreMes(mes) : '—'}`}
            </button>
            <button onClick={() => { setLeido(null); setPrevia(null); setArchivo(null); setAsignaciones({}); }} style={s.linkChico}>Cancelar</button>
            {sinMapear.length > 0 && <span style={{ fontSize: 12, color: '#B8862E' }}>{sinMapear.length} sin ligar</span>}
          </div>
        </>
      )}
    </>
  );
}

// ═══════════ HISTORIAL ═══════════
function TabHistorial({ historial }) {
  const [seleccion, setSeleccion] = useState([]);
  const meses = historial.meses;
  const productos = useMemo(() => {
    const m = {};
    historial.data.forEach((r) => {
      const k = r.nombre_excel;
      if (!m[k]) m[k] = { nombre: k, canal: r.canal, costos: {} };
      m[k].costos[new Date(r.mes).toISOString().slice(0, 7)] = Number(r.costo_pieza);
      if (r.canal) m[k].canal = r.canal;
    });
    return Object.values(m).sort((a, b) => String(a.canal).localeCompare(String(b.canal)) || a.nombre.localeCompare(b.nombre, 'es'));
  }, [historial]);

  useEffect(() => { if (!seleccion.length && productos.length) setSeleccion(productos.slice(0, 4).map((p) => p.nombre)); }, [productos]); // eslint-disable-line react-hooks/exhaustive-deps

  const configGrafica = useMemo(() => ({
    type: 'line',
    data: {
      labels: meses.map((m) => nombreMes(`${m}-01`)),
      datasets: productos.filter((p) => seleccion.includes(p.nombre)).map((p, i) => ({
        label: p.nombre, data: meses.map((m) => p.costos[m] ?? null), borderColor: PALETA[i % PALETA.length], backgroundColor: PALETA[i % PALETA.length], tension: 0.2, spanGaps: true, pointRadius: 4,
      })),
    },
    options: { maintainAspectRatio: false, plugins: { legend: { position: 'bottom' }, tooltip: { callbacks: { label: (c) => `${c.dataset.label}: ${pesos(c.raw)}` } } }, scales: { y: { ticks: { callback: (v) => `$${v}` } } } },
  }), [meses, productos, seleccion]);

  function excel() {
    const filas = productos.map((p) => {
      const f = { Producto: p.nombre, Canal: p.canal || '' };
      meses.forEach((m, i) => {
        f[nombreMes(`${m}-01`)] = p.costos[m] ?? '';
        const prev = i > 0 ? p.costos[meses[i - 1]] : undefined;
        if (i > 0) f[`Var. ${nombreMes(`${m}-01`)}`] = prev && p.costos[m] !== undefined ? Math.round(((p.costos[m] - prev) / prev) * 1000) / 10 : '';
      });
      return f;
    });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(filas), 'Historial costos');
    XLSX.writeFile(wb, 'Historial_costos_PT.xlsx');
  }

  if (!productos.length) return <div style={{ ...s.tarjeta, color: '#8A7690' }}>Todavía no hay costos cargados.</div>;

  return (
    <>
      <div style={s.tarjeta}>
        <div style={{ ...s.headerFila, marginBottom: 10 }}>
          <div style={s.seccionTitulo}>Costo por pieza en el tiempo</div>
          <button onClick={excel} style={{ ...s.botonGuardarChico, display: 'flex', alignItems: 'center', gap: 6 }}><FiDownload /> Excel</button>
        </div>
        {meses.length < 2 && <div style={{ fontSize: 12, color: '#8A7690', marginBottom: 8 }}>Con un solo mes cargado la gráfica es un punto; se arma conforme se suban los siguientes.</div>}
        <Grafica config={configGrafica} />
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
          {productos.map((p) => (
            <button key={p.nombre} onClick={() => setSeleccion((prev) => (prev.includes(p.nombre) ? prev.filter((n) => n !== p.nombre) : [...prev, p.nombre]))}
              style={{ ...s.subTabBtn, ...(seleccion.includes(p.nombre) ? s.subTabBtnActivo : {}), fontSize: 11 }}>
              {p.nombre}
            </button>
          ))}
        </div>
      </div>

      <div style={s.tablaScroll}>
        <table style={s.tabla}>
          <thead>
            <tr>
              <th style={s.th}>Producto</th>
              <th style={s.th}>Canal</th>
              {meses.map((m) => <th key={m} style={{ ...s.th, textAlign: 'right' }}>{nombreMes(`${m}-01`)}</th>)}
            </tr>
          </thead>
          <tbody>
            {productos.map((p) => (
              <tr key={p.nombre}>
                <td style={{ ...s.td, whiteSpace: 'normal', fontWeight: 600 }}>{p.nombre}</td>
                <td style={s.td}>{p.canal || '—'}</td>
                {meses.map((m, i) => {
                  const v = p.costos[m];
                  const prev = i > 0 ? p.costos[meses[i - 1]] : undefined;
                  const d = v !== undefined && prev ? ((v - prev) / prev) * 100 : null;
                  return (
                    <td key={m} style={{ ...s.td, textAlign: 'right' }}>
                      {v === undefined ? '—' : pesos(v)}
                      {d !== null && Math.abs(d) >= 0.05 && <div style={{ fontSize: 10.5, color: d > 0 ? '#B23A48' : '#3E7C5A', fontWeight: Math.abs(d) >= 20 ? 700 : 400 }}>{d > 0 ? '▲' : '▼'} {Math.abs(d).toFixed(1)}%</div>}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ═══════════ MAPEO ═══════════
function TabMapeo({ mapeo, onCambio, onMensaje, errorTexto }) {
  const [editando, setEditando] = useState({});
  const [guardandoUpc, setGuardandoUpc] = useState(null);
  const [filtro, setFiltro] = useState('');
  const visibles = mapeo.data.filter((a) => {
    const q = filtro.trim().toLowerCase();
    return !q || `${a.articulo} ${a.codigo_upc} ${a.nombre_excel || ''}`.toLowerCase().includes(q);
  });
  const compartidos = mapeo.data.filter((a) => a.upc_compartido && a.en_inventario).length;
  const sinMapeo = mapeo.data.filter((a) => a.en_inventario && !a.nombre_excel).length;

  async function guardar(a) {
    try {
      setGuardandoUpc(a.articulo);
      await api.put(`${BASE}/mapeo`, { articulo: a.articulo, nombre_excel: editando[a.articulo] ?? null });
      setEditando((prev) => { const n = { ...prev }; delete n[a.articulo]; return n; });
      onMensaje({ tipo: 'ok', texto: `✓ ${a.articulo} actualizado.` });
      await onCambio();
    } catch (err) {
      console.error(err);
      onMensaje({ tipo: 'error', texto: errorTexto(err, 'No se pudo guardar.') });
    } finally {
      setGuardandoUpc(null);
    }
  }

  return (
    <>
      <datalist id="nombres-excel">{mapeo.nombres_excel.map((n) => <option key={n} value={n} />)}</datalist>
      <div style={{ ...s.filtrosBar, alignItems: 'center' }}>
        <input type="text" placeholder="Buscar artículo, UPC o nombre del Excel…" value={filtro} onChange={(e) => setFiltro(e.target.value)} style={{ ...s.filtroInput, minWidth: 260 }} />
        <span style={{ fontSize: 12, color: sinMapeo ? '#B8862E' : '#3E7C5A' }}>{sinMapeo ? `${sinMapeo} artículo(s) del inventario sin ligar` : 'Todo el inventario está ligado'}</span>
        {compartidos > 0 && <span style={{ fontSize: 12, color: '#6E4F80' }}>· {compartidos} artículos comparten UPC con otro (revisa que cada uno tenga su costo)</span>}
      </div>
      <div style={s.tablaScroll}>
        <table style={s.tabla}>
          <thead>
            <tr>
              <th style={s.th}>Artículo (NetSuite)</th>
              <th style={s.th}>UPC</th>
              <th style={s.th}>Categoría</th>
              <th style={s.th}>Nombre en el Excel de costos</th>
              <th style={{ ...s.th, textAlign: 'right' }}>Costo vigente</th>
              <th style={s.th}></th>
            </tr>
          </thead>
          <tbody>
            {visibles.map((a) => {
              const valor = editando[a.articulo] ?? a.nombre_excel ?? '';
              const cambiado = a.articulo in editando && editando[a.articulo] !== (a.nombre_excel ?? '');
              return (
                <tr key={a.articulo} style={!a.nombre_excel && a.en_inventario ? { background: '#FFF8EC' } : undefined}>
                  <td style={{ ...s.td, whiteSpace: 'normal' }}>{a.articulo}{!a.en_inventario && <div style={x.sub}>sin inventario hoy</div>}</td>
                  <td style={s.td}>{a.codigo_upc}{a.upc_compartido && <div style={{ ...x.sub, color: '#B8862E' }}>UPC compartido</div>}</td>
                  <td style={s.td}>{a.categoria || '—'}</td>
                  <td style={s.td}>
                    <input type="text" list="nombres-excel" value={valor} placeholder="Sin ligar" onChange={(e) => setEditando({ ...editando, [a.articulo]: e.target.value })} style={{ ...s.inputChico, minWidth: 220 }} />
                  </td>
                  <td style={{ ...s.td, textAlign: 'right' }}>
                    {a.costo_pieza === null ? '—' : pesos(a.costo_pieza)}
                    {a.costo_mes && <div style={x.sub}>{nombreMes(a.costo_mes)}</div>}
                  </td>
                  <td style={s.td}>
                    {cambiado && <button onClick={() => guardar(a)} disabled={guardandoUpc === a.articulo} style={s.botonGuardarChico}>{guardandoUpc === a.articulo ? '...' : 'Guardar'}</button>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div style={{ fontSize: 11.5, color: '#8A7690', marginTop: 8 }}>
        El nombre debe ser exactamente como viene en el Excel (escoge de la lista). Varios artículos pueden usar el mismo nombre (por ejemplo la versión en inglés y en español del mismo producto).
        Deja el campo vacío y guarda para quitar el costo de un artículo.
      </div>
    </>
  );
}

// ═══════════ SEMÁFORO ═══════════
function TabSemaforo({ semaforo, onCambio, onMensaje, errorTexto }) {
  const actual = semaforo.data;
  const [form, setForm] = useState({ rojo_hasta: '', amarillo_hasta: '', motivo: '' });
  const [guardando, setGuardando] = useState(false);
  useEffect(() => { if (actual) setForm({ rojo_hasta: actual.rojo_hasta, amarillo_hasta: actual.amarillo_hasta, motivo: '' }); }, [actual]);

  async function guardar() {
    const r = Number(form.rojo_hasta);
    const a = Number(form.amarillo_hasta);
    if (!(r >= 1) || !(a > r)) { onMensaje({ tipo: 'error', texto: 'El rojo debe terminar en 1 día o más y el amarillo después del rojo.' }); return; }
    if (!window.confirm(`Nuevo semáforo: rojo 1–${r}, amarillo ${r + 1}–${a}, verde ${a + 1}+ días. Aplica de inmediato en Caducidades y el Resumen General. ¿Continuar?`)) return;
    try {
      setGuardando(true);
      await api.put(`${BASE}/semaforo`, { rojo_hasta: r, amarillo_hasta: a, motivo: form.motivo });
      onMensaje({ tipo: 'ok', texto: '✓ Rangos del semáforo actualizados.' });
      await onCambio();
    } catch (err) {
      console.error(err);
      onMensaje({ tipo: 'error', texto: errorTexto(err, 'No se pudieron guardar los rangos.') });
    } finally {
      setGuardando(false);
    }
  }

  const r = Number(form.rojo_hasta) || 0;
  const a = Number(form.amarillo_hasta) || 0;
  return (
    <>
      {actual && (
        <div style={s.kgrid}>
          <div style={{ ...s.kcard, borderTop: '3px solid #B23A48' }}><div style={s.kLbl}>🔴 Rojo</div><div style={{ ...s.kVal, fontSize: 24, color: '#B23A48' }}>1–{actual.rojo_hasta} días</div></div>
          <div style={{ ...s.kcard, borderTop: '3px solid #B8862E' }}><div style={s.kLbl}>🟡 Amarillo</div><div style={{ ...s.kVal, fontSize: 24, color: '#B8862E' }}>{actual.rojo_hasta + 1}–{actual.amarillo_hasta} días</div></div>
          <div style={{ ...s.kcard, borderTop: '3px solid #3E7C5A' }}><div style={s.kLbl}>🟢 Verde</div><div style={{ ...s.kVal, fontSize: 24, color: '#3E7C5A' }}>{actual.amarillo_hasta + 1}+ días</div></div>
          <div style={{ ...s.kcard, borderTop: '3px solid #7A2333' }}><div style={s.kLbl}>Vencido</div><div style={{ ...s.kVal, fontSize: 24, color: '#7A2333' }}>0 días o menos</div></div>
        </div>
      )}
      <div style={s.tarjeta}>
        <div style={s.seccionTitulo}>Cambiar rangos</div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={s.filtroGrupo}><span style={s.filtroLabel}>Rojo hasta (días)</span><input type="number" min="1" value={form.rojo_hasta} onChange={(e) => setForm({ ...form, rojo_hasta: e.target.value })} style={{ ...s.filtroInput, width: 110 }} /></div>
          <div style={s.filtroGrupo}><span style={s.filtroLabel}>Amarillo hasta (días)</span><input type="number" min="2" value={form.amarillo_hasta} onChange={(e) => setForm({ ...form, amarillo_hasta: e.target.value })} style={{ ...s.filtroInput, width: 110 }} /></div>
          <div style={{ ...s.filtroGrupo, flex: 1, minWidth: 220 }}><span style={s.filtroLabel}>Motivo del cambio</span><input type="text" value={form.motivo} onChange={(e) => setForm({ ...form, motivo: e.target.value })} style={s.filtroInput} placeholder="Ej. acuerdo de la junta de Logística" /></div>
          <button onClick={guardar} disabled={guardando} style={s.botonGuardarChico}>{guardando ? 'Guardando...' : 'Guardar rangos'}</button>
        </div>
        {r >= 1 && a > r && <div style={{ fontSize: 12, color: '#6E4F80', marginTop: 8 }}>Quedaría: rojo 1–{r} · amarillo {r + 1}–{a} · verde {a + 1}+ días.</div>}
      </div>
      <div style={s.seccionTitulo}>Historial de cambios</div>
      <div style={s.tablaScroll}>
        <table style={s.tabla}>
          <thead><tr><th style={s.th}>Desde</th><th style={s.th}>Rojo</th><th style={s.th}>Amarillo</th><th style={s.th}>Verde</th><th style={s.th}>Motivo</th><th style={s.th}>Cambió</th></tr></thead>
          <tbody>
            {semaforo.historial.map((h, i) => (
              <tr key={h.id} style={i === 0 ? { background: '#F3FAF5' } : undefined}>
                <td style={s.td}>{fmtFechaHora(h.cambiado_en)}{i === 0 && <div style={{ fontSize: 10.5, color: '#3E7C5A', fontWeight: 700 }}>vigente</div>}</td>
                <td style={s.td}>1–{h.rojo_hasta}</td>
                <td style={s.td}>{h.rojo_hasta + 1}–{h.amarillo_hasta}</td>
                <td style={s.td}>{h.amarillo_hasta + 1}+</td>
                <td style={{ ...s.td, whiteSpace: 'normal' }}>{h.motivo || '—'}</td>
                <td style={s.td}>{h.cambiado_por || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ═══════════ BITÁCORA ═══════════
function TabBitacora({ cargas }) {
  if (!cargas.length) return <div style={{ ...s.tarjeta, color: '#8A7690' }}>Todavía no se ha cargado ningún archivo.</div>;
  return (
    <div style={s.tablaScroll}>
      <table style={s.tabla}>
        <thead><tr><th style={s.th}>Cargado</th><th style={s.th}>Mes</th><th style={s.th}>Archivo</th><th style={{ ...s.th, textAlign: 'right' }}>Productos</th><th style={s.th}>Cargó</th></tr></thead>
        <tbody>
          {cargas.map((c) => (
            <tr key={c.id}>
              <td style={s.td}>{fmtFechaHora(c.cargado_en)}</td>
              <td style={s.td}>{nombreMes(c.mes)}{c.reemplazo && <div style={x.sub}>reemplazó una carga anterior</div>}</td>
              <td style={{ ...s.td, whiteSpace: 'normal' }}>{c.archivo || '—'}</td>
              <td style={{ ...s.td, textAlign: 'right' }}>{c.productos}</td>
              <td style={s.td}>{c.cargado_por || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ ...s.seccionKpiBloque, marginTop: 14 }}>
        <div style={{ display: 'flex', gap: 8 }}><FiInfo style={{ color: '#4A1E5C', flexShrink: 0, marginTop: 2 }} />
          <div style={s.seccionKpiFormula}>Caducidades usa el costo del mes más reciente cargado para cada producto. Si un mes no trae algún producto, se queda con su último costo conocido.</div>
        </div>
      </div>
    </div>
  );
}

const x = {
  sub: { fontSize: 10.5, color: '#8A7690' },
  chip: { display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, padding: '2px 6px 2px 8px', margin: '2px 4px 2px 0', borderRadius: 10, background: '#EFE9F3', color: '#4A1E5C' },
  chipX: { background: 'none', border: 'none', cursor: 'pointer', color: '#6E4F80', display: 'flex', padding: 0 },
};